import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ShoppingCart,
  Star,
  LocalShipping,
  AccessTime,
  LocalDining,
  CheckCircle
} from '@mui/icons-material';
import { categories, products, bannerData } from '../data/dummyData';
import MainService from '../services/MainService';

// Import config from MainService
import config from '../services/config.json';

const Home = ({ cart, addToCart }) => {
  const navigate = useNavigate();

  // API Data States
  const [apiCategories, setApiCategories] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [topSellingProducts, setTopSellingProducts] = useState([]);

  // Cart notification state
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [addedProductName, setAddedProductName] = useState('');

  // Loading States
  const [loading, setLoading] = useState({
    categories: false,
    latestProducts: false,
    offers: false,
    topSelling: false
  });

  // Error States
  const [errors, setErrors] = useState({
    categories: null,
    latestProducts: null,
    offers: null,
    topSelling: null
  });

  // Fallback to dummy data
  const featuredProducts = latestProducts.length > 0 ? latestProducts : products.slice(0, 6);
  const featuredCategories = apiCategories.length > 0 ? apiCategories : categories.slice(0, 4);

  // API Call Functions
  const fetchCategories = async () => {
    setLoading(prev => ({ ...prev, categories: true }));
    setErrors(prev => ({ ...prev, categories: null }));

    try {
      const response = await MainService.CategoriesInDropdown();
      setApiCategories(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setErrors(prev => ({ ...prev, categories: 'Failed to load categories' }));
    } finally {
      setLoading(prev => ({ ...prev, categories: false }));
    }
  };

  const fetchLatestProducts = async () => {
    setLoading(prev => ({ ...prev, latestProducts: true }));
    setErrors(prev => ({ ...prev, latestProducts: null }));

    try {
      const response = await MainService.LatestProductsInHome();
      setLatestProducts(response.data || response);
    } catch (error) {
      console.error('Error fetching latest products:', error);
      setErrors(prev => ({ ...prev, latestProducts: 'Failed to load latest products' }));
    } finally {
      setLoading(prev => ({ ...prev, latestProducts: false }));
    }
  };

  const fetchOffers = async () => {
    setLoading(prev => ({ ...prev, offers: true }));
    setErrors(prev => ({ ...prev, offers: null }));

    try {
      const response = await MainService.OffersInHome();
      setOffers(response.data || response);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setErrors(prev => ({ ...prev, offers: 'Failed to load offers' }));
    } finally {
      setLoading(prev => ({ ...prev, offers: false }));
    }
  };

  const fetchTopSellingProducts = async () => {
    setLoading(prev => ({ ...prev, topSelling: true }));
    setErrors(prev => ({ ...prev, topSelling: null }));

    try {
      const response = await MainService.TopSellingProductsInHome();
      setTopSellingProducts(response.data || response);
    } catch (error) {
      console.error('Error fetching top selling products:', error);
      setErrors(prev => ({ ...prev, topSelling: 'Failed to load top selling products' }));
    } finally {
      setLoading(prev => ({ ...prev, topSelling: false }));
    }
  };

  // Call APIs on component mount
  useEffect(() => {
    fetchCategories();
    fetchLatestProducts();
    fetchOffers();
    fetchTopSellingProducts();
  }, []);

  const handleAddToCart = (product) => {
    addToCart(product);
    setAddedProductName(product.Name || product.name || 'Item');
    setShowCartNotification(true);
  };

  const handleViewProduct = (productId, productName) => {
    navigate(`/product/${productId}?name=${encodeURIComponent(productName)}`);
  };

  const handleViewCategory = (categoryId) => {
    navigate(`/products?categoryId=${categoryId}`);
  };

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Banner Section */}
      <Paper
        sx={{
          position: 'relative',
          height: 400,
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4,
          background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${bannerData.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          color: 'white'
        }}
      >
        <Box sx={{ p: 4, maxWidth: 600 }}>
          <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
            {bannerData.title}
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            {bannerData.subtitle}
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#2E7D32',
              color: 'white',
              px: 4,
              py: 1.5,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#1B5E20',
              },
            }}
            onClick={() => navigate('/products')}
          >
            {bannerData.ctaText}
          </Button>
        </Box>
      </Paper>

      {/* Features Section */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <LocalShipping sx={{ fontSize: 48, color: '#2E7D32', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Fast Delivery
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get your fresh products delivered within 2 hours
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <LocalDining sx={{ fontSize: 48, color: '#2E7D32', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Fresh Products
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sourced directly from local farms and suppliers
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <AccessTime sx={{ fontSize: 48, color: '#2E7D32', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              24/7 Service
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Order anytime, we're here to serve you
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Categories Section */}
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
        Shop by Category
      </Typography>

      {/* Categories Loading */}
      {loading.categories && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <CircularProgress sx={{ color: '#2E7D32' }} />
        </Box>
      )}

      {/* Categories Error */}
      {errors.categories && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.categories}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 6 }}>
        {apiCategories.map((category) => (
          <Grid item xs={12} sm={6} md={3} key={category.Id}>
            <Card
              sx={{
                height: 250,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={() => handleViewCategory(category.Id)}
            >
              <CardMedia
                component="img"
                height="250"
                image={config.API_BASE_URL + category.Icon}
                alt={category.Name}
                sx={{
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  color: 'white',
                  p: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {category.Name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {category.Description}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Featured Products Section */}
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
        Featured Products
      </Typography>

      {/* Products Loading */}
      {loading.latestProducts && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <CircularProgress sx={{ color: '#2E7D32' }} />
        </Box>
      )}

      {/* Products Error */}
      {errors.latestProducts && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.latestProducts}
        </Alert>
      )}

      <Grid container spacing={3}>
        {featuredProducts.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id || product.Id || Math.random()}>
            <Card
              sx={{
                //height: 400,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={config.API_BASE_URL + (product.Image1 || product.image1 || product.image || '')}
                alt={product.Name || product.name || 'Product'}
                sx={{ cursor: 'pointer' }}
                onClick={() => handleViewProduct(product.Id, product.Name )}
              />
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {product.Name || product.name || 'Product Name'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.Size?.Name || 'Size'}  - {product.Category?.Name || product.category?.name || 'Category'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                    {product.OfferPrice && product.Price && product.OfferPrice !== product.Price && (
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        ₹{product.Price || product.price || '0.00'}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                      ₹{product.OfferPrice || product.offerPrice || product.Price || product.price || '0.00'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* View All Button */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/products')}
          sx={{
            borderColor: '#2E7D32',
            color: '#2E7D32',
            px: 4,
            py: 1.5,
            fontWeight: 'bold',
            '&:hover': {
              borderColor: '#1B5E20',
              backgroundColor: 'rgba(46, 125, 50, 0.04)',
            },
          }}
        >
          View All Products
        </Button>
      </Box>

      {/* Offers Section */}
      {offers.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
            Special Offers
          </Typography>

          {/* Offers Loading */}
          {loading.offers && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <CircularProgress sx={{ color: '#2E7D32' }} />
            </Box>
          )}

          {/* Offers Error */}
          {errors.offers && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.offers}
            </Alert>
          )}

          <Grid container spacing={3}>
            {offers.map((offer) => (
              <Grid item xs={12} sm={6} md={4} key={offer.id}>
                <Card
                  sx={{
                    height: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    border: '2px solid #FF8F00',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="180"
                    image={offer.image || 'https://via.placeholder.com/300x180'}
                    alt={offer.title}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewProduct(offer.id)}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {offer.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {offer.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF8F00' }}>
                        {offer.discount}% OFF
                      </Typography>
                      <IconButton
                        color="primary"
                        onClick={() => handleAddToCart(offer)}
                        sx={{
                          backgroundColor: '#2E7D32',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#1B5E20',
                          },
                        }}
                      >
                        <ShoppingCart />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Top Selling Products Section */}
      {topSellingProducts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
            Top Selling Products
          </Typography>

          {/* Top Selling Loading */}
          {loading.topSelling && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <CircularProgress sx={{ color: '#2E7D32' }} />
            </Box>
          )}

          {/* Top Selling Error */}
          {errors.topSelling && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.topSelling}
            </Alert>
          )}

          <Grid container spacing={3}>
            {topSellingProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id || product.Id || Math.random()}>
                <Card
                  sx={{
                    //height: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={config.API_BASE_URL + (product.Image1 || product.image1 || product.image || '')}
                    alt={product.Name || product.name || 'Product'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewProduct(product.Id , product.Name )}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {product.Name || product.name || 'Product Name'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.Size?.Name || 'Size'}  - {product.Category?.Name || product.category?.name || 'Category'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                        {product.OfferPrice && product.Price && product.OfferPrice !== product.Price && (
                          <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            ₹{product.Price || product.price || '0.00'}
                          </Typography>
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                          ₹{product.OfferPrice || product.offerPrice || product.Price || product.price || '0.00'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Cart Notification Snackbar */}
      <Snackbar
        open={showCartNotification}
        autoHideDuration={3000}
        onClose={() => setShowCartNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowCartNotification(false)}
          severity="success"
          icon={<CheckCircle />}
          sx={{ width: '100%' }}
        >
          {addedProductName} added to cart successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Home; 